import { useEffect, useRef, useState } from 'react'
import { Palette, RotateCcw } from 'lucide-react'
import { Select, apiMutate, usePermissions } from './primitives.js'
import { SettingsCrumb } from './SettingsCrumb.js'
import { useSaveBar } from '../shell/editing.js'
import { Link } from '../router.js'
import {
  usePrefsStore, applyPrefs, saveUserPrefs, type Prefs,
} from '../prefs.js'
import { Row, Segmented, AccentPicker } from './appearance-controls.js'
import { MesFavoris } from './MesFavoris.js'

/**
 * /settings/appearance (LOT 5.5.1) — MES PRÉFÉRENCES, rien d'autre.
 *
 * L'identité, le branding et les défauts de l'entreprise vivent désormais
 * dans Paramètres → Général : cette page ne concerne QUE le compte connecté.
 *
 * Mécanique :
 *   · le brouillon = MES surcharges (diff avec les valeurs de l'entreprise) ;
 *   · chaque changement s'APERÇOIT immédiatement, mais rien n'est écrit :
 *     la Save Bar globale apparaît dès qu'un écart RÉEL existe avec l'état
 *     serveur, et disparaît si on revient manuellement aux valeurs d'origine ;
 *   · « Valeur de l'entreprise » sur une ligne retire MON override — c'est
 *     aussi une modification, elle passe par la Save Bar ;
 *   · l'héritage est affiché : une ligne sans override est marquée héritée.
 */

const sameRecord = (a: Record<string, string>, b: Record<string, string>) =>
  Object.keys(a).length === Object.keys(b).length && Object.entries(a).every(([k, v]) => b[k] === v)

export function AppearancePage() {
  const store = usePrefsStore()
  const { authenticated } = usePermissions()

  // Brouillon = l'ensemble de MES surcharges désirées.
  const [draft, setDraft] = useState<Prefs | null>(null)
  const booted = useRef(false)
  useEffect(() => {
    // Le snapshot n'est pris qu'APRÈS la première synchronisation serveur :
    // sinon un brouillon vide comparé aux vraies surcharges fabriquerait un
    // faux dirty au chargement.
    if (!booted.current && store.synced) { setDraft({ ...store.user }); booted.current = true }
  }, [store.synced, store.user])

  const d = draft ?? store.user
  const resolvedPreview = { ...store.defaults, ...d }
  const val = (k: string) => resolvedPreview[k] ?? ''
  const overridden = (k: string) => k in d

  const change = (patch: Prefs) => {
    const next = { ...d, ...patch }
    setDraft(next)
    applyPrefs({ ...store.defaults, ...next })
  }
  const inherit = (...keys: string[]) => {
    const next = { ...d }
    for (const k of keys) delete next[k]
    setDraft(next)
    applyPrefs({ ...store.defaults, ...next })
  }

  const dirty = draft !== null && !sameRecord(d, store.user)
  const diffCount = draft === null ? 0 : (() => {
    const keys = new Set([...Object.keys(d), ...Object.keys(store.user)])
    let n = 0
    for (const k of keys) if (d[k] !== store.user[k]) n += 1
    return n
  })()

  useSaveBar({
    dirty: authenticated && dirty,
    count: diffCount,
    successMessage: 'Préférences enregistrées — elles vous suivront sur vos autres navigateurs.',
    onSave: async () => {
      const r = await saveUserPrefs({ ...d }, apiMutate)
      if (!r.ok) return r.message ?? 'Enregistrement refusé.'
      setDraft({ ...d })
      return true
    },
    onCancel: () => {
      setDraft({ ...store.user })
      applyPrefs(store.resolved)
    },
  })

  const inheritProps = (k: string) => ({
    inherited: !overridden(k),
    onInherit: overridden(k) ? () => inherit(k) : undefined,
  })

  return (
    <main className="lc-page lc-stagger">
      <SettingsCrumb />
      <h1 className="lc-title">Apparence</h1>
      <p className="lc-subtitle">
        Personnalisez votre expérience Gestion — vos choix surchargent les
        défauts de l’entreprise et vous suivent d’un navigateur à l’autre.
      </p>

      {!store.authenticated ? (
        <div className="adm-note" style={{ maxWidth: 680 }}>
          Sans session, les réglages ne s’appliquent qu’à CE navigateur et ne
          sont pas enregistrés. Connectez-vous pour qu’ils suivent votre compte.
        </div>
      ) : null}

      <section className="lc-panel gd-panel" aria-labelledby="ap-mine" style={{ marginTop: 'var(--sp-4)' }}>
        <header className="lc-panel__head">
          <h2 className="lc-section-title" id="ap-mine"><Palette size={16} style={{ verticalAlign: -2, marginRight: 8 }} />Mes préférences</h2>
          <span className="lc-panel__spacer" />
          {Object.keys(d).length > 0 ? (
            <button className="lc-btn lc-btn--ghost" onClick={() => inherit(...Object.keys(d))}
              title="Retire tous vos overrides — à confirmer dans la barre d’enregistrement">
              <RotateCcw size={14} /> Tout remettre aux valeurs de l’entreprise
            </button>
          ) : null}
        </header>
        <div className="lc-panel__body ap-body">
          <div className="ap-group">Thème & couleur</div>
          <Row label="Thème" {...inheritProps('theme')}>
            <Segmented ariaLabel="Thème" value={val('theme')} onChange={(v) => change({ theme: v })}
              options={[{ value: 'system', label: 'Système' }, { value: 'dark', label: 'Sombre' }, { value: 'light', label: 'Clair' }]} />
          </Row>
          {val('theme') === 'light' ? (
            <div className="adm-note">Le thème clair n’a pas encore reçu la passe artistique du sombre — considérez-le comme un aperçu.</div>
          ) : null}
          <Row label="Accent" {...(overridden('accent') || overridden('accent_custom')
            ? { onInherit: () => inherit('accent', 'accent_custom') } : { inherited: true })}>
            <AccentPicker value={val('accent')} custom={val('accent_custom') || '#7C5CFF'}
              onChange={(accent, custom) => change(custom ? { accent, accent_custom: custom } : { accent })} />
          </Row>
          <Row label="Ambiance visuelle" hint="Halos, profondeur, lumière — la structure ne change pas." {...inheritProps('ambience')}>
            <Segmented ariaLabel="Ambiance" value={val('ambience')} onChange={(v) => change({ ambience: v })}
              options={[{ value: 'minimal', label: 'Minimale' }, { value: 'discrete', label: 'Discrète' }, { value: 'premium', label: 'Premium' }, { value: 'immersive', label: 'Immersive' }]} />
          </Row>

          <div className="ap-group">Animations</div>
          <Row label="Mouvement" hint="« Système » suit le réglage de votre appareil (prefers-reduced-motion)." {...inheritProps('motion')}>
            <Segmented ariaLabel="Mouvement" value={val('motion')} onChange={(v) => change({ motion: v })}
              options={[{ value: 'system', label: 'Système' }, { value: 'full', label: 'Complètes' }, { value: 'reduced', label: 'Réduites' }, { value: 'minimal', label: 'Minimales' }]} />
          </Row>

          <div className="ap-group">Confort de lecture</div>
          <Row label="Densité" {...inheritProps('density')}>
            <Segmented ariaLabel="Densité" value={val('density')} onChange={(v) => change({ density: v })}
              options={[{ value: 'comfortable', label: 'Confortable' }, { value: 'compact', label: 'Compacte' }]} />
          </Row>
          <Row label="Échelle du texte" {...inheritProps('scale')}>
            <Segmented ariaLabel="Échelle" value={val('scale')} onChange={(v) => change({ scale: v })}
              options={[{ value: '90', label: '90 %' }, { value: '100', label: '100 %' }, { value: '110', label: '110 %' }, { value: '120', label: '120 %' }]} />
          </Row>
          <Row label="Rayon des surfaces" {...inheritProps('radius')}>
            <Segmented ariaLabel="Rayon" value={val('radius')} onChange={(v) => change({ radius: v })}
              options={[{ value: 'compact', label: 'Compact' }, { value: 'standard', label: 'Standard' }, { value: 'round', label: 'Arrondi' }]} />
          </Row>

          <div className="ap-group">Navigation</div>
          {/* Menu flottant (03/11/2026) : « compact » d'avant vaut « flottant ». */}
          <Row label="Menu" hint="Flottant : il s'ouvre au survol et se referme tout seul. Épinglé : toujours ouvert (aussi avec l'épingle du menu)." {...inheritProps('sidebar')}>
            <Segmented ariaLabel="Menu" value={val('sidebar') === 'pinned' ? 'pinned' : 'auto'} onChange={(v) => change({ sidebar: v })}
              options={[{ value: 'auto', label: 'Flottant' }, { value: 'pinned', label: 'Épinglé' }]} />
          </Row>
          <Row label="Page d’accueil" hint="Votre destination après connexion." {...inheritProps('home')}>
            <Select value={val('home')} onChange={(v) => change({ home: v })} compact
              options={[
                { value: 'dashboard', label: 'Tableau de bord' }, { value: 'rh-access', label: 'RH' },
                { value: 'security', label: 'Sécurité' }, { value: 'parc', label: 'Parc' },
                { value: 'communication', label: 'Communication' }, { value: 'infrastructure', label: 'Infrastructure' },
                { value: 'last', label: 'Reprendre là où j’étais' },
              ]} />
          </Row>
          <Row label="Badge Alertes" hint="Quelles alertes comptent dans la pastille du menu." {...inheritProps('alerts_badge')}>
            <Segmented ariaLabel="Badge alertes" value={val('alerts_badge')} onChange={(v) => change({ alerts_badge: v })}
              options={[{ value: 'all', label: 'Toutes' }, { value: 'action', label: 'À traiter et plus' }, { value: 'critical', label: 'Critiques' }]} />
          </Row>
          <Row label="Menu clic droit" hint="Actions rapides au clic droit, à la touche Menu ou à l’appui long. Maj + clic droit garde le menu du navigateur." {...inheritProps('context_menu')}>
            <Segmented ariaLabel="Menu clic droit" value={val('context_menu')} onChange={(v) => change({ context_menu: v })}
              options={[{ value: 'on', label: 'Activé' }, { value: 'off', label: 'Menu du navigateur' }]} />
          </Row>

          <div className="ap-group">Tableau de bord</div>
          <Row label="Carte « Ce soir »" {...inheritProps('dashboard_show_tonight')}>
            <Segmented ariaLabel="Ce soir" value={val('dashboard_show_tonight')} onChange={(v) => change({ dashboard_show_tonight: v })}
              options={[{ value: 'true', label: 'Afficher' }, { value: 'false', label: 'Masquer' }]} />
          </Row>
          <Row label="Familles non configurées" {...inheritProps('dashboard_show_unconfigured')}>
            <Segmented ariaLabel="Familles non configurées" value={val('dashboard_show_unconfigured')} onChange={(v) => change({ dashboard_show_unconfigured: v })}
              options={[{ value: 'true', label: 'Afficher' }, { value: 'false', label: 'Masquer' }]} />
          </Row>

          <div className="ap-group">Langue & formats</div>
          <Row label="Langue"><span className="lc-pill lc-pill--idle">Français — seule langue disponible</span></Row>
          <Row label="Format d’heure" {...inheritProps('hour_format')}>
            <Segmented ariaLabel="Heure" value={val('hour_format')} onChange={(v) => change({ hour_format: v })}
              options={[{ value: '24', label: '24 h' }, { value: '12', label: '12 h' }]} />
          </Row>
          <Row label="Format de date" {...inheritProps('date_format')}>
            <Segmented ariaLabel="Date" value={val('date_format')} onChange={(v) => change({ date_format: v })}
              options={[{ value: 'short', label: '21/08/2026' }, { value: 'long', label: '21 août 2026' }, { value: 'iso', label: '2026-08-21' }]} />
          </Row>
          <Row label="Temps relatif" hint="« il y a 4 min » ou l’horodatage précis." {...inheritProps('relative_time')}>
            <Segmented ariaLabel="Temps relatif" value={val('relative_time')} onChange={(v) => change({ relative_time: v })}
              options={[{ value: 'auto', label: 'Automatique' }, { value: 'relative', label: 'Relatif' }, { value: 'precise', label: 'Précis' }]} />
          </Row>

          <div className="ap-group">Accessibilité</div>
          <Row label="Contraste renforcé" hint="Bordures et textes secondaires relevés — les états gardent toujours icône + texte." {...inheritProps('contrast')}>
            <Segmented ariaLabel="Contraste" value={val('contrast')} onChange={(v) => change({ contrast: v })}
              options={[{ value: 'normal', label: 'Normal' }, { value: 'high', label: 'Renforcé' }]} />
          </Row>
          <Row label="Transparence" hint="« Réduite » : surfaces opaques, flous désactivés." {...inheritProps('transparency')}>
            <Segmented ariaLabel="Transparence" value={val('transparency')} onChange={(v) => change({ transparency: v })}
              options={[{ value: 'normal', label: 'Normale' }, { value: 'reduced', label: 'Réduite' }]} />
          </Row>
          <Row label="Focus clavier renforcé" {...inheritProps('focus_ring')}>
            <Segmented ariaLabel="Focus" value={val('focus_ring')} onChange={(v) => change({ focus_ring: v })}
              options={[{ value: 'normal', label: 'Normal' }, { value: 'strong', label: 'Renforcé' }]} />
          </Row>
        </div>
      </section>

      {/* Les favoris de la barre latérale (24/10/2026) : hors Save Bar, chaque geste s'applique tout de suite. */}
      {store.authenticated ? <MesFavoris /> : null}

      <div className="adm-note" style={{ maxWidth: 680, marginTop: 'var(--sp-4)' }}>
        L’identité de l’instance, le branding et les défauts de l’entreprise
        ont déménagé dans <Link href="/settings/general">Paramètres → Général</Link>.
      </div>
    </main>
  )
}
